import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, from, Observable, of, Subject, switchMap, takeUntil, tap } from "rxjs";
import { environment } from "src/environments/environment";
import { BaseResponse } from "../model/response/base-response";
import { AppInfoRequest, DarkModeResponse, DateFormatsString } from "../model/global-interface";
import { TinyColor } from "@ctrl/tinycolor";
import { AppGlobalSettings } from "../features/settings/settings";
import { IdbService } from "./idb.service";
import { SettingsService } from "../features/settings/settings.service";
import moment from "moment";

@Injectable({
    providedIn: 'root',
})
export class GlobalService {

    private readonly url = `${environment.apiUrl}/app-info`;

    imageChange: Subject<string> = new Subject<string>(); //subject for changing image on sidebar
    closeSidebar: Subject<void> = new Subject<void>(); //subject for closing sidebar
    blockScroll: Subject<boolean> = new Subject<boolean>(); //subject for blocking scroll

    isMobile$ = new Subject<boolean>(); //subject for checking if it's mobile 

    //REGEX FOR DECIMALS
    decimalRegex = /^\d{1,10}(\,\d{1,2})?$/;
    decimalRegexNoLimit = /^\d+(,\d+)?$/;


    constructor(
        private http: HttpClient,
        private idbService: IdbService,
        private settingsService: SettingsService,
    ) {

    }


    getAppInfoAndClearCache(currentVersion?: string): Observable<BaseResponse> {
        let uri = `${this.url}/version`;

        if (currentVersion != undefined) {
            uri = uri + `?currentVersion=${currentVersion}`;
        }
        return this.http.get<BaseResponse>(uri);
    }

    cleanAppData(request: AppInfoRequest): Observable<BaseResponse> {
        return this.http.put<BaseResponse>(`${this.url}/clean`, request);
    }


    returnRgbColor(color: string, darken?: boolean, lighten?: boolean, value?: number): string {

        if (darken && value) {
            return new TinyColor(color).darken(value).toRgbString().replaceAll("rgb", "").replaceAll(",", " ").replaceAll('(', '').replaceAll(')', '')
        }

        else if (lighten && value) {
            return new TinyColor(color).lighten(value).toRgbString().replaceAll("rgb", "").replaceAll(",", " ").replaceAll('(', '').replaceAll(')', '')

        }
        return new TinyColor(color).toRgbString().replaceAll("rgb", "").replaceAll(",", " ").replaceAll('(', '').replaceAll(')', '')


    }


    /**
     * Play a sound, the sounds are in this folder
     * assets/audio/notifications/     
     * @param audioName the name of the mp3 to play     
     */
    playSound(audioName: string) {
        let audio = new Audio();
        audio.src = `./assets/audio/notifications/${audioName}.mp3`;
        audio.load();
        audio.play();
    }

    isDark(): boolean {
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return true;
        } else {
            return false;
        }
    }

    isMobile(): boolean {
        if (window.innerHeight > window.innerWidth) {
            return true;
        } else {
            return false;
        }
    }


    dark(setDark: boolean) {

        document.documentElement.style.setProperty('--color-background-dark', new TinyColor('#828282').mix('var(--color-primary-800)', 20).setAlpha(0.3).toRgbString());

        let themeLink = document.getElementById('theme-css') as HTMLLinkElement;

        //let imageElement = document.getElementById('app-logo-sidebar') as HTMLImageElement;

        if (themeLink) {


            if (!setDark) {


                themeLink.href = environment.theme.lightThemePath;
                document.body.classList.remove('dark-theme');


                document.documentElement.style.setProperty('--color-background', '#fff');
                document.documentElement.style.setProperty('--color-text', '#000');
                document.documentElement.style.setProperty('--color-input', '#d3d8dc');
                //this.logoPath = environment.logo;
                this.imageChange.next(environment.logo)
                //imageElement.src = environment.logo;


            } else {


                themeLink.href = environment.theme.darkThemePath;
                document.body.classList.add('dark-theme');


                var primaryColor = new TinyColor(document.documentElement.style.getPropertyValue('--color-primary-800'));

                document.documentElement.style.setProperty('--color-background', new TinyColor('#828282').mix(primaryColor, 20).setAlpha(0.3).toRgbString());
                document.documentElement.style.setProperty('--color-text', '#fff');
                document.documentElement.style.setProperty('--color-input', new TinyColor('#212121').mix(primaryColor, 20).toRgbString());

                //this.logoPath = environment.logoDark;
                //imageElement.src = environment.logoDark;
                this.imageChange.next(environment.logoDark)
            }


        }

    }


    themeColors() {
        document.documentElement.style.setProperty('--color-primary', new TinyColor(environment.appColor).toString());
        document.documentElement.style.setProperty('--color-primary-950', new TinyColor(environment.appColor).darken(36).toString());
        document.documentElement.style.setProperty('--color-primary-900', new TinyColor(environment.appColor).darken(32).toString());
        document.documentElement.style.setProperty('--color-primary-800', new TinyColor(environment.appColor).darken(24).toString());
        document.documentElement.style.setProperty('--color-primary-700', new TinyColor(environment.appColor).darken(16).toString());
        document.documentElement.style.setProperty('--color-primary-600', new TinyColor(environment.appColor).darken(8).toString());
        document.documentElement.style.setProperty('--color-primary-500', new TinyColor(environment.appColor).toString());
        document.documentElement.style.setProperty('--color-primary-400', new TinyColor(environment.appColor).lighten(8).toString());
        document.documentElement.style.setProperty('--color-primary-300', new TinyColor(environment.appColor).lighten(16).toString());
        document.documentElement.style.setProperty('--color-primary-200', new TinyColor(environment.appColor).lighten(24).toString());
        document.documentElement.style.setProperty('--color-primary-100', new TinyColor(environment.appColor).lighten(32).toString());
        document.documentElement.style.setProperty('--color-primary-50', new TinyColor(environment.appColor).lighten(36).toString());
document.documentElement.style.setProperty('--color-primary-elevation', new TinyColor(environment.appColor).lighten(16).setAlpha(0.3).toString());
        document.documentElement.style.setProperty('--color-primary-elevation-hover', new TinyColor(environment.appColor).lighten(16).setAlpha(0.6).toString());

        document.documentElement.style.setProperty('--color-primaryt', new TinyColor(environment.appColor).toRgbString().replaceAll("rgb", "").replaceAll(",", " ").replaceAll('(', '').replaceAll(')', ''));
        document.documentElement.style.setProperty('--color-primary-950t', new TinyColor(environment.appColor).darken(36).toRgbString().replaceAll("rgb", "").replaceAll(",", " ").replaceAll('(', '').replaceAll(')', ''));
        document.documentElement.style.setProperty('--color-primary-900t', new TinyColor(environment.appColor).darken(32).toRgbString().replaceAll("rgb", "").replaceAll(",", " ").replaceAll('(', '').replaceAll(')', ''));
        document.documentElement.style.setProperty('--color-primary-800t', new TinyColor(environment.appColor).darken(24).toRgbString().replaceAll("rgb", "").replaceAll(",", " ").replaceAll('(', '').replaceAll(')', ''));
        document.documentElement.style.setProperty('--color-primary-700t', new TinyColor(environment.appColor).darken(16).toRgbString().replaceAll("rgb", "").replaceAll(",", " ").replaceAll('(', '').replaceAll(')', ''));
        document.documentElement.style.setProperty('--color-primary-600t', new TinyColor(environment.appColor).darken(8).toRgbString().replaceAll("rgb", "").replaceAll(",", " ").replaceAll('(', '').replaceAll(')', ''));
        document.documentElement.style.setProperty('--color-primary-500t', new TinyColor(environment.appColor).toRgbString().replaceAll("rgb", "").replaceAll(",", " ").replaceAll('(', '').replaceAll(')', ''));
        document.documentElement.style.setProperty('--color-primary-400t', new TinyColor(environment.appColor).lighten(8).toRgbString().replaceAll("rgb", "").replaceAll(",", " ").replaceAll('(', '').replaceAll(')', ''));
        document.documentElement.style.setProperty('--color-primary-300t', new TinyColor(environment.appColor).lighten(16).toRgbString().replaceAll("rgb", "").replaceAll(",", " ").replaceAll('(', '').replaceAll(')', ''));
        document.documentElement.style.setProperty('--color-primary-200t', new TinyColor(environment.appColor).lighten(24).toRgbString().replaceAll("rgb", "").replaceAll(",", " ").replaceAll('(', '').replaceAll(')', ''));
        document.documentElement.style.setProperty('--color-primary-100t', new TinyColor(environment.appColor).lighten(32).toRgbString().replaceAll("rgb", "").replaceAll(",", " ").replaceAll('(', '').replaceAll(')', ''));
        document.documentElement.style.setProperty('--color-primary-50t', new TinyColor(environment.appColor).lighten(36).toRgbString().replaceAll("rgb", "").replaceAll(",", " ").replaceAll('(', '').replaceAll(')', ''));
    }

    /**
     * This function try to parse a number
     * @param input 
     * @param defaultValue 
     * @returns the string converted to numner, ord the default value (0)
     */
    TryParseNumber(input: string, defaultValue: number = 0): number {
        const parsedValue = parseInt(input, 10);
        return isNaN(parsedValue) ? defaultValue : parsedValue;
    }


    GetGlobalSettings(): Observable<AppGlobalSettings | undefined> {
        let globalSettings: AppGlobalSettings | undefined = undefined;

        return from(this.idbService.getAllData(environment.idbTables.GlobalSettings)).pipe(
            switchMap((res: any) => {
                //console.warn("\n \n \n GOT THE DATA FROM IDB FOR SETTINGS \n \n \n");
                //console.warn(res);

                if (res !== undefined && res.length > 0) {
                    // Return the settings from IDB
                    console.info("SETTINGS ARE SET lol");
                    globalSettings = res[0].item;
                    return of(globalSettings);
                } else {
                    console.info("SETTINGS ARE NOT SET xD");
                    return of(undefined);
                }
            }),
            //takeUntil(this.theEnd$) // Ensure the observable completes when needed
        );
    }

    roundToTwo(num: number) {
        return Math.round((num + Number.EPSILON) * 100) / 100;
    }

    formatDecimalToDot(num: string): number {
        console.log("num", num);
        return +Number(num.replace(',', '.'));
    }

    formatDecimalToComma(num: number): string {
        return num.toFixed(2).replace('.', ',');
    }


    validateBase64(url: string): boolean {
        const m = /^data:([a-z]+\/[a-z0-9+.\-]+);base64,(.*)$/i.exec(url);
        if (!m) return false;
        return this.isValidBase64(m[2]);
    }

    isValidBase64(raw: string): boolean {
        const cleaned = raw.replace(/\s+/g, "");


        const basic = /^[A-Za-z0-9+/]+={0,2}$/.test(cleaned);
        if (!basic) return false;


        if (cleaned.length % 4 !== 0) return false;


        try {
            atob(cleaned);
            return true;
        } catch {
            return false;
        }
    }

    getIdbTableKey(tableName: string, userId: string): string {
        let code = `${userId}-${tableName}-dropdownsData`;
        return code;
    }

}